import { Routes } from '@angular/router';

import { RootRedirectComponent } from './components/root-redirect/root-redirect.component';
import { HomePageComponent } from './components/home-page/home-page.component';
import { VerifyEmailModalComponent } from './components/verify-email-modal/verify-email-modal.component';
import { EmailVerificationComponent } from './components/email-verification/email-verification.component';

import { CheckoutSubscriptionComponent } from './components/subscriptions/checkout-subscription/checkout-subscription.component';
import { AdminUserProfileComponent } from './components/admin-components/admin-user-profile/admin-user-profile.component';

import { AuthGuard } from './core/guards/auth.guard';
import { BillingAccessGuard } from './core/guards/billing-access.guard';
import { PolicyGuard } from './core/guards/policy.guard';
import { Permission } from './core/auth/permissions';
import { RedirectAuthedToHomeGuard } from './core/guards/redirect-authed-to-home.guard';
import { TllGuestLoginGuard } from './core/guards/tll-guest-login.guard';

import { UiModeGuard, UiModeCanMatch } from './core/guards/ui-mode.guard';
import { environment } from '../environments/environment';

/**
 * Application routes configuration.
 *
 * Onboarding Flow (post-registration, Learner only):
 * 1. /onboarding/select-categories — user picks their interest categories
 * 2. /onboarding/recommended-courses — recommended courses shown based on interests
 *
 * The topbar is hidden for /onboarding routes (see AppComponent.hideTopBar).
 * Both routes are protected by AuthGuard — only authenticated users can visit them.
 *
 * Guide Tour Integration:
 * - The Guide Tour modal opens automatically when ?guideTour=open is present in URL
 * - After Stripe checkout success, redirect to: /user-management?guideTour=open
 * - The GuideTourService handles the query parameter parsing
 */
export const routes: Routes = [
  { path: '', component: RootRedirectComponent, pathMatch: 'full' },

  { path: 'set-password', component: HomePageComponent, canMatch: [RedirectAuthedToHomeGuard] },
  { path: 'welcome', component: VerifyEmailModalComponent },
  { path: 'verify-email', component: EmailVerificationComponent },
  {
    path: 'auth/complete',
    loadComponent: () =>
      import('./components/auth-complete/auth-complete.component').then((m) => m.AuthCompleteComponent),
  },
  {
    // TLL redirect (Auth Code + PKCE) sign-in landing — distinct from the SK12 partner hand-off above.
    path: 'auth/landing',
    loadComponent: () => import('./components/auth-landing/auth-landing.component').then((m) => m.AuthLandingComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./components/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
  },

  {
    // TLL LearnWorlds migration page: sign-in / registration entry point during the migration.
    // Reached via MigrationRoutingService; with the routing off it continues straight to B2C.
    path: 'get-started',
    loadComponent: () =>
      import('./components/account-check/account-check.component').then((m) => m.AccountCheckComponent),
    canMatch: [() => environment.features?.learnWorldsRouting ?? false, RedirectAuthedToHomeGuard],
  },

  {
    path: 'register/free',
    loadComponent: () => import('./components/register-modal/register.component').then((m) => m.RegisterComponent),
    canMatch: [RedirectAuthedToHomeGuard],
  },
  {
    path: 'register/paid/:planId',
    loadComponent: () => import('./components/register-modal/register.component').then((m) => m.RegisterComponent),
    canMatch: [RedirectAuthedToHomeGuard],
  },

  // ── Onboarding flow (post-registration, Learner) ──────────────────────────
  // No UiModeGuard here — the onboarding flow intentionally has no topbar/admin chrome.
  {
    // Personalized Recommendations onboarding, step 1 (state / grades / subject).
    path: 'onboarding/certification-goals',
    loadComponent: () =>
      import('./components/onboarding/certification-goals/certification-goals.component').then(
        (m) => m.CertificationGoalsComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    // Personalized Recommendations onboarding, step 2 (recommended courses).
    path: 'onboarding/certification-recommendations',
    loadComponent: () =>
      import('./components/onboarding/certification-recommendations/certification-recommendations.component').then(
        (m) => m.CertificationRecommendationsComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'onboarding/select-categories',
    loadComponent: () =>
      import('./components/onboarding/select-categories/select-categories.component').then(
        (m) => m.SelectCategoriesComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'onboarding/recommended-courses',
    loadComponent: () =>
      import('./components/onboarding/recommended-courses/recommended-courses-onboarding.component').then(
        (m) => m.RecommendedCoursesOnboardingComponent,
      ),
    canActivate: [AuthGuard],
  },

  // ── Learner area ──────────────────────────────────────────────────────────
  {
    path: 'dashboard',
    loadComponent: () => import('./components/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    canActivate: [AuthGuard, UiModeGuard],
  },

  {
    path: 'choose-memberships',
    loadComponent: () =>
      import('./components/subscriptions/subscription-user-page/subscription-user-page.component').then(
        (m) => m.SubscriptionUserPageComponent,
      ),
  },
  {
    path: 'choose-memberships/register',
    loadComponent: () =>
      import('./components/subscriptions/subscription-user-page/subscription-user-page.component').then(
        (m) => m.SubscriptionUserPageComponent,
      ),
    canMatch: [RedirectAuthedToHomeGuard],
  },
  {
    path: 'choose-memberships/register-promotion',
    loadComponent: () =>
      import('./components/subscriptions/subscription-user-page/subscription-user-page.component').then(
        (m) => m.SubscriptionUserPageComponent,
      ),
  },
  {
    path: 'choose-memberships/register-promotion/:setIndex',
    loadComponent: () =>
      import('./components/subscriptions/subscription-user-page/subscription-user-page.component').then(
        (m) => m.SubscriptionUserPageComponent,
      ),
  },
  {
    path: 'choose-memberships/pre-register',
    loadComponent: () =>
      import('./components/subscriptions/subscription-user-page/subscription-user-page.component').then(
        (m) => m.SubscriptionUserPageComponent,
      ),
    canMatch: [RedirectAuthedToHomeGuard],
  },
  {
    path: 'choose-memberships/upgrade',
    loadComponent: () =>
      import('./components/subscriptions/subscription-user-page/subscription-user-page.component').then(
        (m) => m.SubscriptionUserPageComponent,
      ),
    canMatch: [RedirectAuthedToHomeGuard],
  },
  {
    path: 'choose-memberships/pre-register/course/:courseSlug',
    loadComponent: () =>
      import('./components/subscriptions/subscription-user-page/subscription-user-page.component').then(
        (m) => m.SubscriptionUserPageComponent,
      ),
    canMatch: [RedirectAuthedToHomeGuard],
  },
  {
    path: 'choose-memberships/pre-register/bundle/:bundleSlug',
    loadComponent: () =>
      import('./components/subscriptions/subscription-user-page/subscription-user-page.component').then(
        (m) => m.SubscriptionUserPageComponent,
      ),
    canMatch: [() => environment.features?.learningPaths ?? true, RedirectAuthedToHomeGuard],
  },

  {
    path: 'register/pre-register/course/:courseSlug',
    loadComponent: () => import('./components/register-modal/register.component').then((m) => m.RegisterComponent),
    canMatch: [RedirectAuthedToHomeGuard],
  },
  {
    path: 'register/pre-register/bundle/:bundleSlug',
    loadComponent: () => import('./components/register-modal/register.component').then((m) => m.RegisterComponent),
    canMatch: [() => environment.features?.learningPaths ?? true, RedirectAuthedToHomeGuard],
  },

  // School Signup Flow (Self-Group Signup) — enabled via environment.features.groupSelfSignup
  {
    path: 'school-signup/step1',
    loadComponent: () =>
      import('./components/school-signup/step1-select-plan/step1-select-plan.component').then(
        (m) => m.Step1SelectPlanComponent,
      ),
    canMatch: [() => environment.features?.groupSelfSignup ?? false, RedirectAuthedToHomeGuard],
  },
  {
    path: 'school-signup/step2',
    loadComponent: () =>
      import('./components/school-signup/step2-organization/step2-organization.component').then(
        (m) => m.Step2OrganizationComponent,
      ),
    canMatch: [() => environment.features?.groupSelfSignup ?? false, RedirectAuthedToHomeGuard],
  },
  {
    path: 'school-signup/step3',
    loadComponent: () =>
      import('./components/school-signup/step3-admin-details/step3-admin-details.component').then(
        (m) => m.Step3AdminDetailsComponent,
      ),
    canMatch: [() => environment.features?.groupSelfSignup ?? false, RedirectAuthedToHomeGuard],
  },
  {
    path: 'school-signup/checkout',
    loadComponent: () =>
      import('./components/school-signup/step4-checkout/step4-checkout.component').then(
        (m) => m.Step4CheckoutComponent,
      ),
    canMatch: [() => environment.features?.groupSelfSignup ?? false],
    canActivate: [AuthGuard],
  },

  { path: 'checkout-subscription', component: CheckoutSubscriptionComponent, canActivate: [AuthGuard] },
  {
    path: 'checkout-course',
    loadComponent: () =>
      import('./components/courses/checkout-course/checkout-course.component').then((m) => m.CheckoutCourseComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'checkout-bundle',
    loadComponent: () =>
      import('./components/bundles/checkout-bundle/checkout-bundle.component').then((m) => m.CheckoutBundleComponent),
    canMatch: [() => environment.features?.learningPaths ?? true],
    canActivate: [AuthGuard],
  },
  {
    path: 'checkout-success',
    loadComponent: () =>
      import('./components/checkout-success/checkout-success.component').then((m) => m.CheckoutSuccessComponent),
  },
  {
    path: 'checkout-course-success',
    loadComponent: () =>
      import('./components/courses/checkout-course-success/checkout-course-success.component').then(
        (m) => m.CheckoutCourseSuccessComponent,
      ),
  },
  {
    path: 'subscription-success',
    loadComponent: () =>
      import('./components/subscriptions/subscription-success/subscription-success.component').then(
        (m) => m.CheckoutSubscriptionSuccessComponent,
      ),
  },

  // Public course catalog (no UiMode)
  {
    path: 'course-catalog',
    loadComponent: () =>
      import('./components/courses/course-list/course-list.component').then((m) => m.CourseListComponent),
    canActivate: [TllGuestLoginGuard],
  },

  // ── Admin zone ────────────────────────────────────────────────────────────
  {
    path: 'user-management',
    loadComponent: () =>
      import('./components/admin-components/user-management/user-management.component').then(
        (m) => m.UserManagementComponent,
      ),
    canMatch: [PolicyGuard, UiModeCanMatch],
    data: { anyPerm: [Permission.ViewUserManagement] },
  },
  {
    path: 'create-user',
    loadComponent: () =>
      import('./components/admin-components/components/create-user-form/create-user-form.component').then(
        (m) => m.CreateUserFormComponent,
      ),
    canMatch: [PolicyGuard, UiModeCanMatch],
    data: {
      anyPerm: [Permission.ViewUserManagement],
      roles: ['SuperAdmin', 'SupportAdmin', 'GroupAdmin', 'SubGroupAdmin'],
    },
  },
  {
    path: 'create-user/:userKey',
    loadComponent: () =>
      import('./components/admin-components/components/create-user-form/create-user-form.component').then(
        (m) => m.CreateUserFormComponent,
      ),
    canMatch: [PolicyGuard, UiModeCanMatch],
    data: {
      anyPerm: [Permission.ViewUserManagement],
      roles: ['SuperAdmin', 'SupportAdmin', 'GroupAdmin', 'SubGroupAdmin'],
    },
  },
  {
    path: 'product-management',
    loadComponent: () =>
      import('./components/admin-components/product-management/product-management.component').then(
        (m) => m.ProductManagementComponent,
      ),
    canMatch: [PolicyGuard, UiModeCanMatch],
    data: { anyPerm: [Permission.ManageProducts] },
  },
  {
    path: 'category-management',
    loadComponent: () =>
      import('./components/admin-components/category-management/category-management/category-management.component').then(
        (m) => m.CategoryManagementComponent,
      ),
    canMatch: [PolicyGuard, UiModeCanMatch],
    data: { anyPerm: [Permission.ManageCategories] },
  },

  // Post-course completion screen (learner) — congratulations + what to take next.
  // Guards deliberately omitted, exactly like /course-details and /learning-path-details: the
  // screen is reached by a full page load right after the course player, before the auth
  // bootstrap has settled, and a guard that waits on authReady$ there loses the deep link.
  {
    path: 'course-completed/:slug',
    loadComponent: () =>
      import('./components/courses/course-completion/course-completion.component').then(
        (m) => m.CourseCompletionComponent,
      ),
  },

  // Course / bundle detail pages (public)
  {
    path: 'course-details/:slug',
    loadComponent: () =>
      import('./components/courses/course-details/course-details.component').then((m) => m.CourseDetailsComponent),
  },
  {
    path: 'learning-path-details/:slug',
    loadComponent: () =>
      import('./components/bundles/bundle-details/bundle-details.component').then((m) => m.BundleDetailsComponent),
    canMatch: [() => environment.features?.learningPaths ?? true],
  },

  // User profile (learner / admin)
  {
    path: 'profile',
    loadComponent: () =>
      import('./components/profile/profile-wrapper/profile.component').then((m) => m.ProfileComponent),
    canActivate: [AuthGuard, UiModeGuard],
    children: [
      {
        path: 'details',
        loadComponent: () =>
          import('./components/profile/user-details/user-details.component').then((m) => m.UserDetailsComponent),
      },
      {
        path: 'billing',
        loadComponent: () => import('./components/profile/billing/billing.component').then((m) => m.BillingComponent),
        canActivate: [BillingAccessGuard],
      },
      {
        path: 'security',
        loadComponent: () =>
          import('./components/profile/security/security.component').then((m) => m.SecurityComponent),
      },
      {
        // TLL: edit teaching preferences (state/grade/subject) — reuses the onboarding step in profile mode.
        path: 'certification-goals',
        loadComponent: () =>
          import('./components/onboarding/certification-goals/certification-goals.component').then(
            (m) => m.CertificationGoalsComponent,
          ),
        data: { mode: 'profile' },
      },
      { path: '', pathMatch: 'full', redirectTo: 'details' },
    ],
  },

  // Admin user profile
  {
    path: 'admin/user/:userKey/profile',
    component: AdminUserProfileComponent,
    canMatch: [PolicyGuard, UiModeCanMatch],
    data: { anyPerm: [Permission.ViewUserManagement] },
    children: [
      {
        path: 'details',
        loadComponent: () =>
          import('./components/profile/user-details/user-details.component').then((m) => m.UserDetailsComponent),
      },
      {
        path: 'billing',
        loadComponent: () =>
          import('./components/admin-components/admin-user-profile/admin-billing/admin-billing.component').then(
            (m) => m.AdminBillingComponent,
          ),
      },
      {
        path: 'courses',
        loadComponent: () =>
          import('./components/admin-components/admin-user-profile/user-courses/user-courses.component').then(
            (m) => m.UserCoursesComponent,
          ),
      },
      { path: '', redirectTo: 'details', pathMatch: 'full' },
    ],
  },

  // Other learner pages
  {
    path: 'my-learning',
    loadComponent: () => import('./components/my-learning/my-learning.component').then((m) => m.MyLearningComponent),
    canActivate: [AuthGuard, UiModeGuard],
  },
  {
    path: 'my-achievements',
    loadComponent: () =>
      import('./components/my-achievements/my-achievements.component').then((m) => m.MyAchievementsComponent),
    canActivate: [AuthGuard, UiModeGuard],
  },
  {
    path: 'settings',
    loadComponent: () => import('./components/settings/general/general.component').then((m) => m.GeneralComponent),
    canActivate: [AuthGuard, UiModeGuard],
  },

  // Admin lazy modules
  {
    path: 'groups',
    loadChildren: () => import('./components/groups/groups.routes').then((m) => m.routes),
    canMatch: [PolicyGuard, UiModeCanMatch],
    data: { anyPerm: [Permission.ViewGroups] },
  },
  {
    path: 'memberships',
    loadComponent: () =>
      import('./components/subscriptions/subscription-list/subscription-list.component').then(
        (m) => m.SubscriptionListComponent,
      ),
    canMatch: [PolicyGuard, UiModeCanMatch],
    data: { anyPerm: [Permission.ManageMemberships] },
  },
  {
    path: 'reports',
    loadChildren: () => import('./components/admin-components/reports/reports.routes').then((m) => m.routes),
    canMatch: [PolicyGuard, UiModeCanMatch],
    data: { anyPerm: [Permission.Reports] },
  },

  { path: '**', redirectTo: '' },
];